import type { ClassData, Student, StudentPrivate } from '../../types';
import { assignPseudonyms, type Pseudonym } from './pseudonyms';

/**
 * Build the one-way rewrite that replaces a roster's identifying data with
 * pseudonyms.
 *
 * PURE ON PURPOSE. It returns the updates to apply; the caller writes them.
 * That keeps the decision about WHAT is erased reviewable and testable on its
 * own, separately from the Firestore mechanics — and this is a change a teacher
 * cannot undo, so the decision deserves to be readable in one place.
 *
 * WHAT IS REMOVED: first name, last name, email. The email goes because it is
 * the identifier a school directory, a mail merge or a support ticket would
 * match on — leaving it while renaming the student is theatre.
 *
 * WHAT IS DELIBERATELY KEPT:
 *  - gcUserId, which is what keeps Google Classroom working. Google already
 *    knows who this is; the id tells LUC nothing a name would.
 *  - the student's LUC id, so every grade, submission and attendance record
 *    still points at the same person.
 *  - studentNumber, which the teacher may need for their own SIS and which
 *    they can clear separately if they want it gone.
 *
 * WHAT THIS IS NOT. It is pseudonymisation, not anonymisation. The roster keeps
 * its alphabetical order by design, so anyone holding the real class list can
 * re-identify every student by position. That is a deliberate trade for a
 * roster a teacher can actually use, and the UI has to say so.
 */

/** Fields cleared outright. Parent contact and OEN live in the private roster
 *  and are cleared through the same call, not left behind here. */
export const SANITISED_FIELDS = [
  'email', 'parentName', 'parentEmail', 'parentPhone', 'governmentId',
  /**
   * Added after a review found them surviving on the classmate-readable row.
   *
   * `preferredName` is the sharpest: it is not a hidden field, it is RENDERED — "Goes by …" in the
   * student panel, on report names and on printouts — so a sanitised roster showed a pseudonym next
   * to the child's actual everyday name. `photoUrl` is a face. `guidanceEmail` is an address.
   * `pronouns` goes because it is volunteered personal information that no longer belongs to a
   * pseudonymous record.
   *
   * `studentNumber` is deliberately still KEPT — a teacher needs it for their own SIS — and the
   * modal's "Kept" row says so, which is the difference between a trade and an omission.
   */
  'preferredName', 'photoUrl', 'guidanceEmail', 'pronouns',
] as const;

export interface SanitisePlan {
  /** studentId → the fields to write. Empty when there is nothing to do. */
  updates: Map<string, Partial<Student>>;
  /** Students already pseudonymous, left untouched — reported, not hidden. */
  alreadyClean: string[];
  /** Students no pseudonym could be assigned to. */
  failed: string[];
}

/**
 * Has this student already been through it?
 *
 * An explicit flag, set by the sanitiser itself. The first version inferred it
 * — "their name is in the set of issued pseudonyms AND they have no email" —
 * and the production caller passed EVERY current name as the issued set, so the
 * name half was always true and the rule collapsed to "has no email".
 *
 * That skipped, permanently and silently: students whose Workspace admin hides
 * email addresses (Google returns none, so the import lands real names with no
 * email), students added by hand without one, and any student whose email a
 * teacher had cleared. A whole roster imported without emails reported "nothing
 * left to replace" with twenty-five real names on screen.
 */
export const looksSanitised = (s: Student): boolean => s.pseudonymised === true;

export function planSanitise(students: Student[], alreadyIssued?: Pseudonym[]): SanitisePlan {
  // Words in use by students ALREADY sanitised, so a second run cannot reissue
  // one. Taken from the flagged students only — reading every current name
  // would feed real names into the "already used" set.
  const issuedFrom = alreadyIssued ?? issuedPseudonyms(students.filter(looksSanitised));
  const issuedFirst = new Set(issuedFrom.map(p => p.firstName));
  const issuedLast = new Set(issuedFrom.map(p => p.lastName));

  // Only students still carrying real data are touched. Re-running after one new
  // student joins must not churn the twenty-five already done — their pseudonyms
  // are what the teacher has learned.
  const todo = students.filter(s => !looksSanitised(s));
  const { assignments, exhausted } = assignPseudonyms(todo, {
    alreadyUsed: { firstNames: issuedFirst, lastNames: issuedLast },
  });

  const updates = new Map<string, Partial<Student>>();
  for (const s of todo) {
    const p = assignments.get(s.id);
    if (!p) continue;
    /**
     * BOTH STAMPS. `pseudonymised` is this feature's own flag and `looksSanitised` reads it;
     * `placeholderName` is the vault's, and it is the one signal `namesAlreadySafe` is meant to rest
     * on. A sanitised class can be sealed Privacy First, and with only the sanitiser's flag the vault
     * had to INFER that these rows were placeholders — which it got wrong for four review rounds
     * running. Writing both costs a boolean and removes the inference for every class sanitised from
     * here on; the vault reads both for the ones already written.
     */
    const patch: Partial<Student> = {
      firstName: p.firstName, lastName: p.lastName, pseudonymised: true, placeholderName: true,
    };
    // Cleared to '' rather than deleted: an absent field reads as "never had
    // one", and a teacher looking at a sanitised roster should see it is empty
    // because it was emptied.
    for (const f of SANITISED_FIELDS) (patch as Record<string, unknown>)[f] = '';
    updates.set(s.id, patch);
  }

  return {
    updates,
    alreadyClean: students.filter(looksSanitised).map(s => s.id),
    failed: exhausted,
  };
}

/** Pseudonyms already issued in this class, read back off the roster so a second
 *  run never reissues a word. */
export const issuedPseudonyms = (students: Student[]): Pseudonym[] =>
  students.map(s => ({ firstName: (s.firstName || '').trim(), lastName: (s.lastName || '').trim() }))
    .filter(p => p.firstName && p.lastName);

/**
 * Students carrying parent contact details, which a sanitise clears.
 *
 * Parent data is never imported by any roster path — it is typed by hand — so
 * clearing it destroys the teacher's own work. It still has to go: these fields
 * sit on the SAME student row, so "Owl Ridge — parent: sarah.okafor@gmail.com"
 * re-identifies the child immediately, and a partial sanitise is the worst
 * outcome because it looks finished.
 *
 * Counted so the confirmation can warn only when there is something to warn
 * about. On the classes this is actually used for the answer is usually zero.
 */
export function parentContactCount(classData: ClassData): number {
  /**
   * READ THE PRIVATE ROSTER, NOT THE STUDENT ROW.
   *
   * parentName/parentEmail/parentPhone/governmentId are STUDENT_PII_FIELDS:
   * they are stripped off the classmate-readable class doc and stored in
   * classes/{id}/private/roster. hydrateStudentPrivate merges only
   * MERGE_BACK_TO_STUDENT back onto the in-memory student, and parent fields
   * are not in it — so `student.parentEmail` is ALWAYS undefined and the first
   * version of this counted zero every time. The warning it drives never
   * appeared, while the write destroyed the data anyway.
   */
  const priv = classData.studentPrivate || {};
  return (classData.students || []).filter(s => {
    const p: StudentPrivate | undefined = priv[s.id];
    return (p?.parentName || '').trim() || (p?.parentEmail || '').trim() || (p?.parentPhone || '').trim();
  }).length;
}

/** Students carrying an OEN, which is also cleared. Same private-roster read. */
export function governmentIdCount(classData: ClassData): number {
  const priv = classData.studentPrivate || {};
  return (classData.students || []).filter(s => (priv[s.id]?.governmentId || '').trim()).length;
}

/**
 * Students on a Classroom-LINKED class with no stable Google id.
 *
 * For these, sanitising breaks everything the confirmation promises: the email
 * was the only join key, so grades stop pushing, submissions stop arriving, and
 * a re-import re-adds them under their real name because neither id nor email
 * matches. Rosters imported before gcUserId was recorded, and hand-added
 * students, are exactly this population.
 */
export function unlinkableCount(classData: ClassData): number {
  if (!classData.gcCourseId) return 0;
  return (classData.students || []).filter(s => !(s.gcUserId || '').trim() && !looksSanitised(s)).length;
}

/** How much identifying data is still on this roster — drives whether the offer
 *  is shown at all. */
export function identifyingCount(classData: ClassData): number {
  // Anyone not yet flagged still has their real name on the row — which is
  // identifying on its own, with or without an email. Keying this on email
  // alone hid entire rosters that were imported without addresses.
  const notDone = (classData.students || []).filter(s => !looksSanitised(s));
  return notDone.length;
}

/**
 * The real names a sanitise would otherwise leave behind on the class document.
 *
 * A student's name is denormalised onto every submission, stream post, reply
 * and quiz attempt they ever made, for display. Renaming the roster row does
 * not touch any of them — so mid-semester, which is exactly when this feature
 * is used, every student who has ever submitted work keeps their real name in
 * Firestore, on a document every classmate's client can read.
 *
 * The teacher UI prefers the CURRENT roster name when rendering, which makes it
 * worse rather than better: they see pseudonyms everywhere and reasonably
 * believe the claim on the confirmation screen.
 *
 * Returns the class-level patch that rewrites them, or null when there is
 * nothing to rewrite.
 */
export function planDenormalisedNameRewrite(
  classData: ClassData,
  updates: Map<string, Partial<Student>>,
): Partial<ClassData> | null {
  if (!updates.size) return null;
  const nameFor = new Map<string, string>();
  for (const [id, patch] of updates) {
    nameFor.set(id, `${patch.firstName || ''} ${patch.lastName || ''}`.trim());
  }

  let changed = false;
  const rename = (id: string | undefined, current: string | undefined): string | undefined => {
    const next = id ? nameFor.get(id) : undefined;
    if (!next || next === current) return current;
    changed = true;
    return next;
  };

  const submissions = (classData.submissions || []).map(sub => ({
    ...sub,
    studentName: rename(sub.studentId, sub.studentName) ?? sub.studentName,
    // Group work carries a list of names with no ids to match on, so it cannot
    // be rewritten per member. Dropped rather than left holding real names.
    ...(sub.groupMemberNames?.length ? { groupMemberNames: [] as string[] } : {}),
  }));
  if ((classData.submissions || []).some(s => s.groupMemberNames?.length)) changed = true;

  /**
   * THE TITLE CARRIES THE NAME TOO, and it was the half this function missed.
   *
   * A student question is posted with `title: \`Question from ${first} ${last}\``, by both the portal
   * and `student-class-write`. So a class that had been through this rewrite still had every
   * questioner's real name sitting in `stream[i].title` on the classmate-readable document, while
   * `fullyProtected` reported true and the copy said the readable copy had been deleted.
   *
   * Rewritten by PREFIX rather than by replacing the whole title, so a post whose title is something
   * else entirely is left alone.
   */
  const QUESTION_PREFIX = 'Question from ';
  const stream = (classData.stream || []).map((post) => {
    const next = {
      ...post,
      authorName: rename(post.authorId, post.authorName) ?? post.authorName,
      // Replies carry no author id, so there is nothing to match a student to.
      // They keep the name that was written at the time; the confirmation says so
      // rather than the code pretending otherwise.
    };
    const newName = post.authorId ? nameFor.get(post.authorId) : undefined;
    if (newName && typeof post.title === 'string' && post.title.startsWith(QUESTION_PREFIX)) {
      const retitled = `${QUESTION_PREFIX}${newName}`;
      if (retitled !== post.title) { next.title = retitled; changed = true; }
    }
    return next;
  });

  const attempts = (classData.popQuizAttempts || []).map(a => ({
    ...a,
    studentName: rename(a.studentId, a.studentName) ?? a.studentName,
  }));

  if (!changed) return null;
  return {
    ...(classData.submissions ? { submissions } : {}),
    ...(classData.stream ? { stream } : {}),
    ...(classData.popQuizAttempts ? { popQuizAttempts: attempts } : {}),
  } as Partial<ClassData>;
}
